import React from 'react';

import { TextField } from '@mui/material';
import _ from 'es-toolkit/compat';
import { NumberFormatValues, PatternFormat } from 'react-number-format';

import { Nullable } from '@clearverify/types';

import { getFieldError, getFieldWarning, hasFieldError } from 'src/utils/finalForm';

import type { MaskedTextFieldProps } from './interface';

export const MaskedTextField: React.FC<MaskedTextFieldProps> = ({
  input,
  meta,
  inputProps,
  slotProps,
  className,
  mask,
  formatMask = '_',
  numericValue = true,
  valueFormatter = emptyValueFormatter,
  disabled,
  warning,
  showErrorOnlyTouched = true,
  showErrorWhenDisabled = true,
  ...restProps
}) => {
  const { name, value, onBlur, onChange, focused } = input;

  const handleChange = React.useCallback(
    (value: NumberFormatValues) => {
      onChange(valueFormatter(value.value));
    },
    [onChange, valueFormatter],
  );

  const hasStartAdornment = Boolean(inputProps?.startAdornment);
  const hasEndAdornment = Boolean(inputProps?.endAdornment);
  const hasAnyAdornment = hasStartAdornment || hasEndAdornment;
  const hasValue = !_.isNil(value) && value !== '';
  const shrink = hasValue || hasAnyAdornment || focused;

  const error = hasFieldError(meta, showErrorOnlyTouched);
  const showError = showErrorWhenDisabled || !disabled;
  const helperText = showError
    ? getFieldError(meta, showErrorOnlyTouched) || getFieldWarning(value, meta, warning, disabled)
    : '';

  return (
    <PatternFormat
      className={className}
      valueIsNumericString={numericValue}
      name={name}
      value={valueFormatter(value)}
      customInput={TextField}
      variant="outlined"
      format={mask}
      mask={formatMask}
      onValueChange={handleChange}
      onBlur={onBlur}
      focused={focused}
      disabled={disabled}
      error={showError ? error : false}
      helperText={helperText}
      {...restProps}
      slotProps={{
        ...slotProps,
        input: {
          ...inputProps,
          ...(typeof slotProps?.input === 'object' ? slotProps.input : undefined),
        },
        inputLabel: {
          shrink,
          ...(typeof slotProps?.inputLabel === 'object' ? slotProps.inputLabel : undefined),
        },
      }}
    />
  );
};

// just passes the value as is
const emptyValueFormatter = (value: Nullable<string>) => value;
